import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  Team,
  TeamInput,
  createTeam,
  getTeam,
  updateTeam,
} from "@/features/supply-config/teams-api";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import { toast } from "@/hooks/use-toast";
import { useProducts } from "@/features/forms/hooks/use-products";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";

export default function TeamForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const prefill = (location.state as any)?.prefill as
    | {
        name?: string;
        professional_ids?: string[];
        product_ids?: string[];
        region_id?: string;
        // Where to go after creating (e.g. straight to bundle creation when
        // launching a product). Falls back to team details when absent.
        next?: string;
      }
    | undefined;

  const { data: products } = useProducts();
  const { data: regions } = useActiveRegions();
  const { data: profData } = useAllProfessionals();
  const professionals = profData?.professionals ?? [];

  const { data: existing } = useQuery({
    queryKey: ["team", id],
    queryFn: () => getTeam(id!),
    enabled: isEdit,
  });

  const [form, setForm] = useState<TeamInput>({
    name: prefill?.name ?? "",
    product_ids: prefill?.product_ids ?? [],
    region_id: prefill?.region_id ?? "",
    supported_languages: [],
    status: "ACTIVE",
    professional_ids: prefill?.professional_ids ?? [],
  });
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (isEdit && existing && !hydrated) {
      setForm({
        name: existing.name,
        product_ids: existing.products?.map((p) => p.id) ?? [],
        region_id: existing.region_id,
        supported_languages: existing.supported_languages ?? [],
        status: existing.status,
        professional_ids: existing.professionals?.map((p) => p.id) ?? [],
      });
      setHydrated(true);
    }
  }, [isEdit, existing, hydrated]);

  const productOptions = useMemo(
    () => (products ?? []).map((p: any) => ({ label: p.name, value: p.id })),
    [products],
  );
  const profOptions = useMemo(
    () => professionals.map((p: any) => ({ label: p.name, value: p.id })),
    [professionals],
  );
  const languageOptions = useMemo(() => {
    const selected = new Set(form.professional_ids ?? []);
    const langs = new Set<string>(form.supported_languages ?? []);
    professionals.forEach((p: any) => {
      if (selected.has(p.id))
        (p.languages ?? []).forEach((l: string) => l && langs.add(l));
    });
    return [...langs].sort().map((l) => ({ label: l, value: l }));
  }, [professionals, form.professional_ids, form.supported_languages]);

  const save = useMutation({
    mutationFn: (): Promise<Team> =>
      isEdit ? updateTeam(id!, form) : createTeam(form),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      if (isEdit) qc.invalidateQueries({ queryKey: ["team", id] });
      toast({ title: isEdit ? "Team updated" : "Team created" });
      // Launched-from-product flow: jump straight to bundle creation.
      if (!isEdit && prefill?.next) {
        navigate(prefill.next);
        return;
      }
      const targetId = isEdit ? id : res?.id;
      navigate(targetId ? `../${targetId}` : "..");
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  return (
    <HasAccess permission="write">
      <div className="max-w-2xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">
            {isEdit ? "Edit Team" : "New Team"}
          </h1>
        </div>

        <Card>
          <CardContent className="space-y-4 pt-6">
          <div>
            <p className="text-xs text-gray-500 mb-1">Name</p>
            <Input
              placeholder="Team name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Products</p>
              <MultiSelect
                options={productOptions}
                value={form.product_ids}
                onChange={(ids) => setForm({ ...form, product_ids: ids })}
                placeholder="Select products"
                className="h-auto min-h-10 max-h-40 overflow-y-auto items-start py-2"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Region</p>
              <Select
                value={form.region_id}
                onValueChange={(v) => setForm({ ...form, region_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  {(regions ?? []).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {codeToFlag(r.code)} {r.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">
              Professionals ({form.professional_ids?.length ?? 0})
            </p>
            <MultiSelect
              options={profOptions}
              value={form.professional_ids}
              onChange={(ids) => {
                // Auto-include each selected professional's languages into the
                // team's supported languages (union; still freely editable below).
                const langs = new Set(form.supported_languages ?? []);
                professionals.forEach((p: any) => {
                  if (ids.includes(p.id))
                    (p.languages ?? []).forEach(
                      (l: string) => l && langs.add(l),
                    );
                });
                setForm({
                  ...form,
                  professional_ids: ids,
                  supported_languages: [...langs].sort(),
                });
              }}
              placeholder="Select professionals"
              className="h-auto min-h-10 max-h-40 overflow-y-auto items-start py-2"
            />
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">
              Supported languages{" "}
              <span className="text-gray-400">
                (auto-filled from professionals — editable)
              </span>
            </p>
            <MultiSelect
              options={languageOptions}
              value={form.supported_languages ?? []}
              onChange={(v) => setForm({ ...form, supported_languages: v })}
              placeholder="Select languages"
              className="h-auto min-h-10 max-h-28 overflow-y-auto items-start py-2"
            />
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">Status</p>
            <Select
              value={form.status}
              onValueChange={(v) =>
                setForm({ ...form, status: v as "ACTIVE" | "INACTIVE" })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              disabled={
                save.isPending ||
                !form.name ||
                !form.product_ids.length ||
                !form.region_id
              }
            >
              {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create"}
            </Button>
          </div>
          </CardContent>
        </Card>
      </div>
    </HasAccess>
  );
}
